<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/vue-3';
import type {
	GlobalFieldId,
	GlobalPrimitiveFieldId,
	GlobalSingleRelationFieldId,
} from '#layers/data-model/types/schema';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplayNames, useProjectSchemaStore } from '#imports';

const props = defineProps<NodeViewProps>();

const { t } = useI18n();

const rawFieldId = computed(() => props.node.attrs.rawFieldId as string | null);
const fieldIds = computed(() => {
	const fieldId = props.node.attrs.fieldId as GlobalFieldId | GlobalFieldId[] | null;

	if (!fieldId) return [];
	return Array.isArray(fieldId) ? fieldId : [fieldId];
});

const { findPrimitiveFieldById, findSingleRelationFieldById } = useProjectSchemaStore();
const { getPrimitiveFieldDisplayName, getSingleRelationFieldDisplayName } = useDisplayNames();

const labelParts = computed(() => {
	if (fieldIds.value.length === 0) return [];

	if (fieldIds.value.length === 1) {
		const primitiveField = findPrimitiveFieldById(fieldIds.value[0] as GlobalPrimitiveFieldId);

		if (primitiveField) {
			return [getPrimitiveFieldDisplayName(primitiveField)];
		}

		const singleRelationField = findSingleRelationFieldById(fieldIds.value[0] as GlobalSingleRelationFieldId);

		if (singleRelationField) {
			return [getSingleRelationFieldDisplayName(singleRelationField)];
		}

		return [];
	}

	const labels: string[] = [];

	for (const [index, fieldId] of fieldIds.value.entries()) {
		const isLast = index === fieldIds.value.length - 1;

		const singleRelationField = findSingleRelationFieldById(fieldId as GlobalSingleRelationFieldId);

		if (singleRelationField) {
			labels.push(getSingleRelationFieldDisplayName(singleRelationField));
			continue;
		}

		if (!isLast) break;

		const primitiveField = findPrimitiveFieldById(fieldId as GlobalPrimitiveFieldId);

		if (!primitiveField) break;

		labels.push(getPrimitiveFieldDisplayName(primitiveField));
	}

	return labels;
});

const unresolved = computed(() => Boolean(rawFieldId.value) || labelParts.value.length !== fieldIds.value.length);
</script>

<template>
	<NodeViewWrapper as="span" class="h-6 shrink-0" data-drag-handle>
		<DBadge :variant="selected ? 'subtle' : 'soft'" contenteditable="false" size="xs" class="text-xs leading-none">
			<span v-if="labelParts.length === 0">{{ t('collection_field_select.unknown_field') }}</span>
			<span v-else class="inline-flex items-center gap-1">
				<template v-for="(label, index) in labelParts" :key="`${label}-${index}`">
					<DIcon v-if="index > 0" name="lucide:chevron-right" class="size-3 text-muted" />
					<span :class="index < labelParts.length - 1 ? 'text-muted' : undefined">{{ label }}</span>
				</template>
				<template v-if="unresolved">
					<DIcon name="lucide:chevron-right" class="size-3 text-muted" />
					<span>{{ t('collection_field_select.unknown_field') }}</span>
				</template>
			</span>
		</DBadge>
	</NodeViewWrapper>
</template>
